/**
 * Staggered pixel-grid hover for primary/secondary .btn elements.
 */
const PIXEL_TRAIL_COLS = 12;
const PIXEL_TRAIL_ROWS = 4;
const PIXEL_TRAIL_STEP = 0.03;

const ROW_OFFSETS = [0, 1.5, 0.5, 2, 1, 2.5];

function pixelTrailVariation(row, col) {
  return ((row * 7 + col * 3) % 5) * 0.2;
}

function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = arr[i];
    arr[i] = arr[j];
    arr[j] = t;
  }
  return arr;
}

function pixelTrailDelayForMode(mode, row, col, permPerCol) {
  const rowOffset = ROW_OFFSETS[row % ROW_OFFSETS.length];
  const variation = pixelTrailVariation(row, col);

  if (mode === "right") {
    return (
      (PIXEL_TRAIL_COLS - 1 - col + rowOffset + variation) * PIXEL_TRAIL_STEP
    );
  }

  if (mode === "center") {
    const centerCol = (PIXEL_TRAIL_COLS - 1) / 2;
    const distanceFromCenter = Math.abs(col - centerCol);
    return (distanceFromCenter + rowOffset + variation) * PIXEL_TRAIL_STEP;
  }

  if (mode === "bottom") {
    const colOffsets = [0, 0.5, 0.25, 0.75, 0.1, 0.6, 0.35, 0.85];
    const colOffset = colOffsets[col % colOffsets.length];
    const rowFromBottom = PIXEL_TRAIL_ROWS - 1 - row;
    return (
      (rowFromBottom + colOffset + variation * 0.5) * (PIXEL_TRAIL_STEP * 1.5)
    );
  }

  if (mode === "shuffle" && permPerCol) {
    return PIXEL_TRAIL_STEP * 0.5 * (col + permPerCol[col][row]);
  }

  return (col + rowOffset + variation) * PIXEL_TRAIL_STEP;
}

function pixelTrailNormalizeMode(raw) {
  if (!raw || typeof raw !== "string") {
    return "cursor";
  }
  const m = raw.trim().toLowerCase().replace(/_/g, "-");
  if (
    m === "left" ||
    m === "right" ||
    m === "center" ||
    m === "bottom" ||
    m === "shuffle" ||
    m === "cursor"
  ) {
    return m;
  }
  return "cursor";
}

function pixelTrailBuildPermPerCol() {
  const per = [];
  for (let c = 0; c < PIXEL_TRAIL_COLS; c++) {
    const rowIdx = [];
    for (let r = 0; r < PIXEL_TRAIL_ROWS; r++) {
      rowIdx.push(r);
    }
    per[c] = shuffle(rowIdx);
  }
  return per;
}

function pixelTrailBuildShuffledRowIndexes() {
  const out = [];
  for (let r = 0; r < PIXEL_TRAIL_ROWS; r++) {
    const cols = [];
    for (let c = 0; c < PIXEL_TRAIL_COLS; c++) {
      cols.push(c);
    }
    out.push(shuffle(cols));
  }
  return out;
}

function pixelTrailAttachInteraction(btn, mode, grid, shuffledRowIndexes) {
  const cells = grid.querySelectorAll(".btn__pixel-cell");

  const applyFromPoint = (clientX, clientY) => {
    const rect = btn.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (w <= 0 || h <= 0) {
      return;
    }
    const cw = w / PIXEL_TRAIL_COLS;
    const ch = h / PIXEL_TRAIL_ROWS;
    let cursorCol = Math.floor((clientX - rect.left) / cw);
    let cursorRow = Math.floor((clientY - rect.top) / ch);
    cursorCol = Math.max(0, Math.min(PIXEL_TRAIL_COLS - 1, cursorCol));
    cursorRow = Math.max(0, Math.min(PIXEL_TRAIL_ROWS - 1, cursorRow));

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      const col = Number(cell.dataset.pixelCol);
      const row = Number(cell.dataset.pixelRow);
      const dist = Math.sqrt((col - cursorCol) ** 2 + (row - cursorRow) ** 2);
      const rowVar =
        shuffledRowIndexes &&
        shuffledRowIndexes[row] &&
        typeof shuffledRowIndexes[row][col] === "number"
          ? shuffledRowIndexes[row][col] * 0.2
          : 0;
      cell.style.setProperty(
        "--pixel-cell-delay",
        `${PIXEL_TRAIL_STEP * (dist + rowVar)}s`
      );
    }
  };

  const restoreCursorDelays = () => {
    for (let j = 0; j < cells.length; j++) {
      const c = cells[j];
      c.style.setProperty("--pixel-cell-delay", `${c.dataset.defaultDelay}s`);
    }
  };

  btn.addEventListener(
    "mouseenter",
    (e) => {
      if (mode === "cursor") {
        applyFromPoint(e.clientX, e.clientY);
      }
      btn.classList.add("btn--pixel-hover");
    },
    { passive: true }
  );

  btn.addEventListener(
    "mouseleave",
    () => {
      btn.classList.remove("btn--pixel-hover");
      if (mode === "cursor") {
        restoreCursorDelays();
      }
    },
    { passive: true }
  );
}

function pixelTrailBuildGrid(btn, mode) {
  const grid = document.createElement("span");
  grid.className = "btn__pixel-grid";
  grid.setAttribute("aria-hidden", "true");

  const permPerCol = mode === "shuffle" ? pixelTrailBuildPermPerCol() : null;
  const shuffledRowIndexes =
    mode === "cursor" ? pixelTrailBuildShuffledRowIndexes() : null;

  const total = PIXEL_TRAIL_ROWS * PIXEL_TRAIL_COLS;
  for (let i = 0; i < total; i++) {
    const row = Math.floor(i / PIXEL_TRAIL_COLS);
    const col = i % PIXEL_TRAIL_COLS;
    const delaySec =
      mode === "cursor"
        ? pixelTrailDelayForMode("center", row, col, null)
        : pixelTrailDelayForMode(mode, row, col, permPerCol);
    const cell = document.createElement("span");
    cell.className = "btn__pixel-cell";
    cell.style.transitionProperty = "opacity";
    cell.style.transitionDuration = "0s";
    cell.dataset.pixelRow = String(row);
    cell.dataset.pixelCol = String(col);
    cell.dataset.defaultDelay = String(delaySec);
    cell.style.setProperty("--pixel-cell-delay", `${delaySec}s`);
    grid.appendChild(cell);
  }

  pixelTrailAttachInteraction(btn, mode, grid, shuffledRowIndexes);
  return grid;
}

function initButtonPixelTrail() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const candidates = document.querySelectorAll(
    "a.btn.btn-primary, button.btn.btn-primary, a.btn.btn-secondary, button.btn.btn-secondary"
  );

  for (let i = 0; i < candidates.length; i++) {
    const btn = candidates[i];
    if (
      btn.classList.contains("btn--no-pixel-trail") ||
      btn.classList.contains("btn--pixel-trail")
    ) {
      continue;
    }
    if (btn.disabled || btn.classList.contains("disabled")) {
      continue;
    }

    const cs = getComputedStyle(btn);
    btn.style.setProperty("--pixel-rest-bg", cs.backgroundColor);

    const mode = pixelTrailNormalizeMode(btn.getAttribute("data-pixel-trail"));

    const children = [];
    for (let c = 0; c < btn.childNodes.length; c++) {
      children.push(btn.childNodes[c]);
    }

    const grid = pixelTrailBuildGrid(btn, mode);
    const label = document.createElement("span");
    label.className = "btn__label";
    for (let k = 0; k < children.length; k++) {
      label.appendChild(children[k]);
    }

    btn.appendChild(grid);
    btn.appendChild(label);
    btn.classList.add("btn--pixel-trail");
  }
}

export default initButtonPixelTrail;
